import type { AvailabilityRoomContentResponse } from "@/app/types/availability";
import { BookingResponse } from "@/app/types/booking";
import {
  AuthChallengeMessage,
  AuthChallengeResponseToken,
} from "@/app/types/user";
import axios, { AxiosResponse } from "axios";
import { Locale } from "next-intl";
import type { LoginPayload } from "thirdweb/auth";
import { publicEnvConfig } from "../config/config";
import { getReferredBy } from "../services/service/localStorageService";
import { HotelDetail, LiveRoomOfferData, SearchData } from "./types";
// @translations
export type ServiceResponse<T> = {
  error: Error | null;
  data: T | null;
};

export const getSearchData = async (
  locale: Locale,
  tokenId: string | null,
  walletId?: string | null,
  searchId?: string | null
): Promise<ServiceResponse<SearchData>> => {
  try {
    if (!(tokenId && walletId) && !searchId) {
      return {
        error: null,
        data: null,
      };
    }

    const url = searchId
      ? `/api/offers/${searchId}?lang=${locale}`
      : `/api/wallet/${walletId?.toLowerCase()}/hotelRequestToken/${tokenId}?lang=${locale}`;

    const response = await fetch(url, {
      method: "GET",
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data: SearchData = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getAuthChallenge = async (
  locale: Locale,
  walletId: string,
  chainId?: number
): Promise<ServiceResponse<AuthChallengeMessage>> => {
  try {
    const searchParams = new URLSearchParams({ lang: locale });
    if (chainId) {
      searchParams.set("chainId", chainId.toString());
    }

    const response = await fetch(`/api/auth/${walletId}?${searchParams}`);

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log("getAuthChallenge Failed:", error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getUserData = async (locale: Locale) => {
  try {
    const response = await fetch(`/api/user-data?lang=${locale}`, {
      cache: "no-store",
      credentials: "include",
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data ? data : null;
  } catch (error) {
    console.log("Verify User LoggedIn Failed:", error);
    return null;
  }
};

export const removeUserData = async (locale: Locale) => {
  try {
    const response = await fetch(`/api/remove-user-data?lang=${locale}`, {
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data ? data : null;
  } catch (error) {
    console.log("Verify User LoggedIn Failed:", error);
    return null;
  }
};

export const verifySignedMessage = async (
  locale: Locale,
  walletId: string,
  signature: string,
  payload: LoginPayload
): Promise<ServiceResponse<AuthChallengeResponseToken>> => {
  try {
    const response = await fetch(`/api/auth?lang=${locale}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        walletId,
        signature,
        payload,
        referredBy: getReferredBy(),
      }),
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(
        errorResponse.errorMessage || "An error occurred during verification"
      );
    }

    const data = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log("Verification Failed:", error);
    return {
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred during verification"),
      data: null,
    };
  }
};

export const getBookings = async (
  locale: Locale
): Promise<ServiceResponse<BookingResponse[]>> => {
  try {
    const response = await fetch(`/api/bookings?lang=${locale}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(
        errorResponse.errorMessage ||
          "An error occurred during booking data fetch"
      );
    }

    const data: { bookings: BookingResponse[] } = await response.json();

    return {
      error: null,
      data: data?.bookings,
    };
  } catch (error) {
    console.log("Booking data fetch Failed:", error);
    return {
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred during booking data fetch"),
      data: null,
    };
  }
};

export const getHotelData = async (
  locale: Locale,
  giataId: string
): Promise<ServiceResponse<HotelDetail>> => {
  try {
    const response = await fetch(
      `/api/hotel-detail/${giataId}?lang=${locale}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data = await response.json();
    return {
      error: null,
      data: data?.hotelDetail,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getMinimalHotelData = async (
  locale: Locale,
  giataId: string
): Promise<ServiceResponse<HotelDetail>> => {
  try {
    const response = await fetch(
      `/api/hotel-detail/${giataId}?type=minimal&lang=${locale}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data = await response.json();
    return {
      error: null,
      data: data?.hotelDetail,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getAvailabilityRoomContent = async (
  locale: Locale,
  id: string,
  code: string
): Promise<ServiceResponse<AvailabilityRoomContentResponse>> => {
  try {
    const response = await fetch(
      `/api/room-content/${id}/${code}?lang=${locale}`
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }
    const data = await response.json();
    return {
      error: null,
      data: data?.hotelDetail,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const fetchAvailabilityRoomContent = async (
  lang: Locale,
  id: string,
  code: string,
  signal: AbortSignal
): Promise<AxiosResponse<{ hotelDetail: AvailabilityRoomContentResponse }>> => {
  return axios(`/api/room-content/${id}/${code}`, {
    params: { lang },
    signal,
  });
};

export const getLiveRoomOfferData = async (
  locale: Locale,
  tokenId: string | null,
  walletId?: string | null,
  providerOfferId?: string,
  providerHotelId?: string
): Promise<ServiceResponse<LiveRoomOfferData>> => {
  try {
    const response = await fetch(
      `${publicEnvConfig.mainApiBaseUrl}/wallet/${walletId}/hotelRequestToken/${tokenId}/${providerOfferId}/${providerHotelId}?lang=${locale}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data: LiveRoomOfferData = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const cancelBooking = async (
  locale: Locale,
  bookingId: string,
  giataId: string,
  params?: string | undefined
): Promise<any> => {
  try {
    const response = await fetch(
      `/api/booking/cancel?bookingId=${bookingId}&giataId=${giataId}&${params ? params : ""}&lang=${locale}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );
    const result = await response.json();
    return result;
  } catch (error: any) {
    return { error: error.message };
  }
};

export async function createPaymentSession(
  locale: Locale,
  bookingId: string,
  source: string,
  platform: string
) {
  const url = `/api/create/session?bookingId=${encodeURIComponent(bookingId)}&source=${encodeURIComponent(source)}&platform=${encodeURIComponent(platform)}&lang=${locale}`;

  // Call the API using POST
  const res = await fetch(url);

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "API call failed");
  }

  return res.json();
}

export const removeBookingOrder = async (
  locale: Locale,
  orderId: string
): Promise<any> => {
  try {
    const response = await fetch(`/api/booking/remove?lang=${locale}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ orderId }),
      credentials: "include",
    });
    const result = await response.json();
    return result;
  } catch (error: any) {
    return { error: error.message };
  }
};

export const getPointsAffiliateData = async (
  locale: Locale
): Promise<ServiceResponse<any[]>> => {
  try {
    const response = await fetch(`/api/user/points-referral?lang=${locale}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(
        errorResponse.errorMessage ||
          "An error occurred during booking data fetch"
      );
    }

    return {
      error: null,
      data: await response.json(),
    };
  } catch (error) {
    console.log("Booking data fetch Failed:", error);
    return {
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred during booking data fetch"),
      data: null,
    };
  }
};

export async function fetchHotelDetailByGiataId(
  locale: Locale,
  hotelId: string
) {
  if (!hotelId) throw new Error("hotelId is required");

  const res = await fetch(`/api/hotel-detail/${hotelId}?lang=${locale}`);

  if (!res.ok) {
    // Optionally: handle error response JSON
    const error = await res.json().catch(() => ({}));
    throw new Error(error.message || "Failed to fetch hotel details");
  }

  const data = await res.json();
  return data.hotelDetail;
}

export async function getRegionalDestinations(locale: Locale) {
  const res = await fetch(`/api/destinations?lang=${locale}`);

  if (!res.ok) {
    // Optionally: handle error response JSON
    const error = await res.json().catch(() => ({}));
    throw new Error(error.message || "Failed to fetch hotel details");
  }

  const data = await res.json();
  return data;
}
